import { useEffect, useRef } from 'react'

/*
 * The homepage hero dot field.
 *
 * Reproduces the capture's own inline script exactly. That script:
 *
 *   1. fetches an SVG holding one <path id="compound"> of 2,873 subpaths
 *   2. splits it on `M` and rebuilds each subpath as its own <path data-index>
 *   3. pulses 8–13 random dots every ~1.5–2.1s, staggered
 *   4. adds .hovered-strong / -medium / -weak by pointer distance (60/120/180
 *      SVG units)
 *
 * `.pulse` and the `.hovered-*` classes are styled by the page's own inline
 * CSS, ported to src/pages/css/home.css.
 *
 * The SVG is vendored to /wf/backsplash-dots.svg rather than fetched from the
 * GitHub raw URL the original used, so the hero does not depend on a third
 * party being up.
 */
export function HeroDots() {
  const mounted = useRef(false)

  useEffect(() => {
    if (mounted.current) return
    mounted.current = true

    const container = document.querySelector('.home-hero-container')
    if (!container || container.querySelector('.svg-container')) return

    let cancelled = false
    const timers: number[] = []
    let interval = 0
    let svg: SVGSVGElement | null = null
    let onMove: ((e: MouseEvent) => void) | null = null

    fetch('/wf/backsplash-dots.svg')
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((markup) => {
        if (cancelled) return
        const holder = document.createElement('div')
        holder.innerHTML = markup
        const svgContainer = holder.querySelector('.svg-container')
        if (!svgContainer) return
        container.prepend(svgContainer)

        svg = svgContainer.querySelector<SVGSVGElement>('#dots')
        const compound = svgContainer.querySelector('#compound')
        if (!svg || !compound) return

        const fullD = compound.getAttribute('d') ?? ''
        const subpaths = fullD.split(/(?=M)/g).filter((p) => p.trim().length > 0)
        compound.remove()

        const ns = 'http://www.w3.org/2000/svg'
        const frag = document.createDocumentFragment()
        subpaths.forEach((d, index) => {
          const path = document.createElementNS(ns, 'path')
          path.setAttribute('d', d.trim())
          path.setAttribute('data-index', String(index))
          frag.appendChild(path)
        })
        svg.appendChild(frag)

        const paths = Array.from(svg.querySelectorAll('path'))

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (!reduceMotion) {
          const pulse = (dot: Element) => {
            dot.classList.add('pulse')
            timers.push(window.setTimeout(() => dot.classList.remove('pulse'), 400))
          }

          const organicPulse = (groupSize: number, spread = 600) => {
            const picked = new Set<number>()
            while (picked.size < Math.min(groupSize, paths.length)) {
              picked.add(Math.floor(Math.random() * paths.length))
            }
            let i = 0
            picked.forEach((index) => {
              const delay = Math.floor(Math.random() * spread) + i * 50
              timers.push(window.setTimeout(() => pulse(paths[index]), delay))
              i++
            })
          }

          interval = window.setInterval(
            () => organicPulse(8 + Math.floor(Math.random() * 6)),
            1500 + Math.random() * 600,
          )
        }

        // Pointer proximity highlight, in the SVG's own coordinate space.
        const boxes = paths.map((p) => {
          const b = p.getBBox()
          return { path: p, cx: b.x + b.width / 2, cy: b.y + b.height / 2 }
        })

        onMove = (e: MouseEvent) => {
          if (!svg) return
          const ctm = svg.getScreenCTM()
          if (!ctm) return
          const pt = svg.createSVGPoint()
          pt.x = e.clientX
          pt.y = e.clientY
          const { x, y } = pt.matrixTransform(ctm.inverse())

          for (const { path, cx, cy } of boxes) {
            const distance = Math.hypot(cx - x, cy - y)
            path.classList.remove('hovered-strong', 'hovered-medium', 'hovered-weak')
            if (distance < 60) path.classList.add('hovered-strong')
            else if (distance < 120) path.classList.add('hovered-medium')
            else if (distance < 180) path.classList.add('hovered-weak')
          }
        }
        svg.addEventListener('mousemove', onMove)
      })
      .catch(() => {
        /* the hero reads fine without its backdrop */
      })

    return () => {
      cancelled = true
      window.clearInterval(interval)
      for (const t of timers) window.clearTimeout(t)
      if (svg && onMove) svg.removeEventListener('mousemove', onMove)
      document.querySelector('.home-hero-container .svg-container')?.remove()
      mounted.current = false
    }
  }, [])

  return null
}
